// src/sections/notifications/NotificationToast.jsx
// -----------------------------------------------------------------
// iOS-style glass morphism toast notification system.
// Stacks top-right via NotificationToastContainer.
//
// Features:
// - Frosted glass card with backdrop-filter blur
// - iOS bounce-in / slide-up-out animations
// - Priority-based pulse on urgent icon squircle
// - Split action buttons (Dismiss | Action) for actionable items
// - Gradient countdown bar with hover pause
// - Auto-dismiss: urgent 8s, normal 5s, low 4s
// - Iconify line icons (thin, SF Symbols style)
// - 15 verb types including whatsapp_message_received
// -----------------------------------------------------------------

import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, styled, keyframes, GlobalStyles } from '@mui/material';
import Iconify from 'src/components/Iconify';
import { NOTIFICATION_PALETTE } from './notificationTypes';
import { VERB_TO_TYPE } from './normalizeNotification';

// -----------------------------------------------------------------
// Verb → UI config (all 15 verbs)
// -----------------------------------------------------------------

const VERB_CONFIG = {
  callback_request:              { cat: 'Callback Request', icon: 'solar:phone-calling-rounded-bold-duotone', colour: NOTIFICATION_PALETTE.error },
  reminder_overdue:              { cat: 'Overdue',          icon: 'solar:danger-triangle-bold-duotone',       colour: NOTIFICATION_PALETTE.error },
  new_lead:                      { cat: 'New Lead',         icon: 'solar:user-plus-bold-duotone',             colour: NOTIFICATION_PALETTE.info },
  lead_assigned_to_user:         { cat: 'Assigned',         icon: 'solar:user-check-bold-duotone',            colour: NOTIFICATION_PALETTE.primary },
  reminder_assigned_to_user:     { cat: 'Task Assigned',    icon: 'solar:check-square-bold-duotone',          colour: NOTIFICATION_PALETTE.primary },
  reminder_due_today:            { cat: 'Due Today',        icon: 'solar:calendar-bold-duotone',              colour: NOTIFICATION_PALETTE.warning },
  deposit_paid:                  { cat: 'Payment',          icon: 'solar:card-bold-duotone',                  colour: NOTIFICATION_PALETTE.success },
  lead_unassigned_from_user:     { cat: 'Reassigned',       icon: 'solar:refresh-bold-duotone',               colour: NOTIFICATION_PALETTE.warning },
  lead_unassigned:               { cat: 'Unassigned',       icon: 'solar:user-cross-bold-duotone',            colour: NOTIFICATION_PALETTE.warning },
  facebook_post_comment_created: { cat: 'Facebook',         icon: 'solar:chat-round-bold-duotone',            colour: NOTIFICATION_PALETTE.info },
  new_email_reply_received:      { cat: 'Email',            icon: 'solar:letter-bold-duotone',                colour: NOTIFICATION_PALETTE.info },
  lead_treatment_started:        { cat: 'Treatment',        icon: 'solar:medical-kit-bold-duotone',           colour: NOTIFICATION_PALETTE.success },
  treatment_started:             { cat: 'Treatment',        icon: 'solar:medical-kit-bold-duotone',           colour: NOTIFICATION_PALETTE.success },
  lead_restored:                 { cat: 'Restored',         icon: 'solar:history-bold-duotone',               colour: NOTIFICATION_PALETTE.primary },
  phone_number_clicked:          { cat: 'Website',          icon: 'solar:phone-bold-duotone',                 colour: NOTIFICATION_PALETTE.grey },
  gmb_review_created:            { cat: 'Google Review',    icon: 'solar:star-bold-duotone',                  colour: NOTIFICATION_PALETTE.google },
  deposit_not_paid:              { cat: 'Deposit Overdue',  icon: 'solar:card-recive-bold-duotone',           colour: NOTIFICATION_PALETTE.warning },
  whatsapp_message:              { cat: 'WhatsApp',         icon: 'ic:baseline-whatsapp',                     colour: NOTIFICATION_PALETTE.whatsapp },
  whatsapp_message_received:     { cat: 'WhatsApp',         icon: 'ic:baseline-whatsapp',                     colour: NOTIFICATION_PALETTE.whatsapp },
  whatsapp_call_incoming:        { cat: 'WhatsApp Call',    icon: 'solar:phone-calling-rounded-bold-duotone', colour: NOTIFICATION_PALETTE.whatsapp },
  voice_call_ringing:            { cat: 'Incoming Call',    icon: 'solar:phone-calling-rounded-bold-duotone', colour: NOTIFICATION_PALETTE.success },
  lead_follow_up_moved:          { cat: 'Follow Up',        icon: 'solar:double-alt-arrow-up-bold-duotone',   colour: NOTIFICATION_PALETTE.info },
};

function getVerbConfig(verb) {
  return VERB_CONFIG[verb] || { cat: 'Notification', icon: 'solar:bell-bold-duotone', colour: NOTIFICATION_PALETTE.grey };
}

// -----------------------------------------------------------------
// Auto-dismiss durations (ms) by priority
// -----------------------------------------------------------------

const DISMISS_DURATION = {
  urgent: 8000,
  normal: 5000,
  low: 4000,
};

// -----------------------------------------------------------------
// Animations
// -----------------------------------------------------------------

const iosEnter = keyframes`
  0%   { opacity: 0; transform: translateY(-100%) scale(0.9); }
  60%  { opacity: 1; transform: translateY(6px) scale(1.02); }
  80%  { transform: translateY(-2px) scale(0.995); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
`;

const iosExit = keyframes`
  0%   { opacity: 1; transform: translateY(0) scale(1); }
  100% { opacity: 0; transform: translateY(-80px) scale(0.92); }
`;

const countdown = keyframes`
  from { width: 100%; }
  to   { width: 0%; }
`;

const iosPulse = keyframes`
  0%   { box-shadow: 0 1px 3px rgba(0,0,0,0.08), 0 0 0 0 rgba(255,86,48,0.3); }
  70%  { box-shadow: 0 1px 3px rgba(0,0,0,0.08), 0 0 0 8px rgba(255,86,48,0); }
  100% { box-shadow: 0 1px 3px rgba(0,0,0,0.08), 0 0 0 0 rgba(255,86,48,0); }
`;

// -----------------------------------------------------------------
// Global keyframes (needed for inline style animations)
// -----------------------------------------------------------------

export function ToastGlobalStyles() {
  return (
    <GlobalStyles
      styles={{
        '@keyframes toastCountdown': {
          from: { width: '100%' },
          to: { width: '0%' },
        },
      }}
    />
  );
}

// -----------------------------------------------------------------
// Styled components — iOS glass card
// -----------------------------------------------------------------

const GlassCard = styled(Box, {
  shouldForwardProp: (p) => !['exiting', 'animDelay'].includes(p),
})(({ exiting, animDelay }) => ({
  width: 365,
  background: 'rgba(255, 255, 255, 0.82)',
  backdropFilter: 'blur(40px) saturate(180%)',
  WebkitBackdropFilter: 'blur(40px) saturate(180%)',
  borderRadius: 20,
  boxShadow: '0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08), inset 0 0.5px 0 rgba(255,255,255,0.6)',
  overflow: 'hidden',
  cursor: 'pointer',
  position: 'relative',
  border: '0.5px solid rgba(255,255,255,0.5)',
  animation: exiting
    ? `${iosExit} 0.35s cubic-bezier(0.4, 0, 1, 1) forwards`
    : `${iosEnter} 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both`,
  animationDelay: exiting ? '0s' : `${animDelay}s`,
  transition: 'transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
  '&:hover': {
    transform: 'scale(1.02)',
  },
}));

const IconSquircle = styled(Box, {
  shouldForwardProp: (p) => p !== 'isUrgent',
})(({ isUrgent }) => ({
  flexShrink: 0,
  width: 40,
  height: 40,
  borderRadius: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  ...(isUrgent && {
    animation: `${iosPulse} 2s ease-out infinite`,
  }),
}));

const ActionButton = styled('button', {
  shouldForwardProp: (p) => !['isAction', 'accentColour'].includes(p),
})(({ isAction, accentColour }) => ({
  flex: 1,
  fontFamily: "'Public Sans', sans-serif",
  fontSize: 13,
  fontWeight: isAction ? 700 : 600,
  padding: '10px 0',
  border: 'none',
  cursor: 'pointer',
  background: 'transparent',
  color: isAction ? accentColour : '#637381',
  transition: 'background 0.15s',
  letterSpacing: '-0.1px',
  '&:hover': {
    background: 'rgba(0,0,0,0.03)',
  },
}));

// -----------------------------------------------------------------
// NotificationToast component
// -----------------------------------------------------------------

export function NotificationToast({ notification, index, onDismiss, onAction }) {
  const [exiting, setExiting] = useState(false);
  const [hovered, setHovered] = useState(false);
  const timerRef = useRef(null);

  const n = notification;
  const vc = getVerbConfig(n.verb);
  const isUrgent = n.priority === 'urgent';
  const duration = DISMISS_DURATION[n.priority] || DISMISS_DURATION.normal;
  const accentColour = vc.colour?.main || '#C4CDD5';

  const startTimer = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setExiting(true);
      setTimeout(() => onDismiss(n.id), 350);
    }, duration);
  }, [duration, n.id, onDismiss]);

  useEffect(() => {
    startTimer();
    return () => clearTimeout(timerRef.current);
  }, [startTimer]);

  const handleMouseEnter = () => {
    setHovered(true);
    clearTimeout(timerRef.current);
  };

  const handleMouseLeave = () => {
    setHovered(false);
    startTimer();
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    setExiting(true);
    setTimeout(() => onDismiss(n.id), 350);
  };

  const handleCardClick = () => {
    onAction?.(n.id, n.action_link);
  };

  const handleActionClick = (e) => {
    e.stopPropagation();
    onAction?.(n.id, n.action_link);
  };

  const practiceName = n.data?.practice_name || n.action_object?.practice_name;

  return (
    <GlassCard
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleCardClick}
      exiting={exiting}
      animDelay={index * 0.1}
    >
      {/* Top glass edge highlight */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 16,
          right: 16,
          height: '0.5px',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)',
        }}
      />

      {/* Content */}
      <Box sx={{ display: 'flex', gap: 1.5, p: '14px 16px' }}>
        {/* App icon squircle */}
        <IconSquircle isUrgent={isUrgent} sx={{ bgcolor: vc.colour?.lighter || '#F4F6F8' }}>
          <Iconify
            icon={vc.icon}
            width={18}
            sx={{ color: vc.colour?.dark || vc.colour?.main || '#637381' }}
          />
        </IconSquircle>

        {/* Body */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* App name + time (iOS style) */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '3px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: 'text.primary',
                  letterSpacing: '-0.1px',
                }}
              >
                {vc.cat}
              </Typography>
              {isUrgent && (
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: 'error.main',
                    flexShrink: 0,
                    boxShadow: (theme) => `0 0 6px ${theme.palette.error.main}60`,
                  }}
                />
              )}
            </Box>
            <Typography sx={{ fontSize: 11, color: 'text.disabled', fontWeight: 500, letterSpacing: '-0.1px' }}>
              now
            </Typography>
          </Box>

          {/* Practice name (iOS subtitle) */}
          {practiceName && (
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: 'text.secondary',
                mb: '2px',
                letterSpacing: '-0.1px',
              }}
            >
              {practiceName}
            </Typography>
          )}

          {/* Description */}
          <Typography
            sx={{
              fontSize: 13,
              color: 'text.primary',
              lineHeight: 1.4,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              letterSpacing: '-0.1px',
            }}
          >
            {n.description}
          </Typography>
        </Box>
      </Box>

      {/* Action buttons row (iOS notification actions) */}
      {n.requires_action && (
        <Box sx={{ borderTop: '0.5px solid rgba(0,0,0,0.06)', display: 'flex' }}>
          <ActionButton onClick={handleDismiss}>
            Dismiss
          </ActionButton>
          <Box sx={{ width: '0.5px', bgcolor: 'rgba(0,0,0,0.06)' }} />
          <ActionButton
            isAction
            accentColour={isUrgent ? accentColour : '#00A76F'}
            onClick={handleActionClick}
          >
            {n.action_label || 'View'}
          </ActionButton>
        </Box>
      )}

      {/* Countdown bar — subtle, inset, gradient */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 20,
          right: 20,
          height: 2,
          borderRadius: 1,
          bgcolor: 'rgba(0,0,0,0.04)',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            height: '100%',
            borderRadius: 1,
            background: isUrgent
              ? `linear-gradient(90deg, ${NOTIFICATION_PALETTE.error.main}, ${NOTIFICATION_PALETTE.warning.main})`
              : `linear-gradient(90deg, ${NOTIFICATION_PALETTE.primary.main}, ${NOTIFICATION_PALETTE.info.main})`,
            opacity: 0.6,
            animation: `${countdown} ${duration}ms linear forwards`,
            ...(hovered && { animationPlayState: 'paused' }),
          }}
        />
      </Box>
    </GlassCard>
  );
}

NotificationToast.propTypes = {
  notification: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    verb: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    priority: PropTypes.oneOf(['urgent', 'normal', 'low']),
    requires_action: PropTypes.bool,
    action_label: PropTypes.string,
    action_link: PropTypes.string,
    data: PropTypes.object,
    action_object: PropTypes.object,
  }).isRequired,
  index: PropTypes.number,
  onDismiss: PropTypes.func.isRequired,
  onAction: PropTypes.func,
};

NotificationToast.defaultProps = {
  index: 0,
  onAction: undefined,
};

// -----------------------------------------------------------------
// Toast container — stacks toasts top-right
// -----------------------------------------------------------------

export function NotificationToastContainer({ toasts, onDismiss, onAction }) {
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: 99999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 1,
        pointerEvents: 'none',
      }}
    >
      {toasts.map((toast, i) => (
        <Box key={toast.id} sx={{ pointerEvents: 'auto' }}>
          <NotificationToast
            notification={toast}
            index={i}
            onDismiss={onDismiss}
            onAction={onAction}
          />
        </Box>
      ))}
    </Box>
  );
}

NotificationToastContainer.propTypes = {
  toasts: PropTypes.array.isRequired,
  onDismiss: PropTypes.func.isRequired,
  onAction: PropTypes.func,
};

// -----------------------------------------------------------------
// Browser Push helper
// -----------------------------------------------------------------

export function requestPushPermission() {
  if (!('Notification' in window)) return Promise.resolve('denied');
  if (Notification.permission === 'granted') return Promise.resolve('granted');
  if (Notification.permission === 'denied') return Promise.resolve('denied');
  return Notification.requestPermission();
}

export function sendBrowserPush(notification) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  const vc = getVerbConfig(notification.verb);
  const isUrgent = notification.priority === 'urgent';
  const practiceName = notification.data?.practice_name || notification.action_object?.practice_name || 'Dental CRM';

  const n = new Notification(`${vc.cat} — ${practiceName}`, {
    body: notification.description,
    icon: '/favicon/android-chrome-512x512.png',
    tag: notification.id,
    requireInteraction: isUrgent,
    silent: !isUrgent,
    data: { action_link: notification.action_link },
  });

  n.onclick = function handleClick(event) {
    event.preventDefault();
    window.focus();
    if (notification.action_link) {
      window.location.href = `/dashboard/${notification.action_link}`;
    }
    n.close();
  };

  if (!isUrgent) {
    setTimeout(() => n.close(), 5000);
  }
}

// Re-export verb config for the demo page
export { VERB_CONFIG, getVerbConfig, VERB_TO_TYPE };
